import { useCallback, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { find, omit, path, propEq } from 'ramda'
import { useSelector } from 'react-redux'

import { prorataCalculator } from '@/models/auth/ports'
import { breakpoints, colors } from '@/styles'
import { useDispatch } from '@/lib/redux'
import { isEmptyOrNull } from '@/utils'
import { stringify } from '@/utils/qs'
import useMediaQuery from '@/hooks/use-media-query'
import {
  getPaymentPlan,
  usePricingPlans
} from '@/features/Pricing/pricing-plans'
import CurrencyFormatter from '@/components/currency-formatter'
import { FieldRadioButton } from '@/components/form'
import Skeleton from '@/components/skeleton'
import { Label, ParagraphMedium, ParagraphSmall } from '@/components/text'

import CouponSection from './coupon-section'
import {
  FormCard,
  FormCardInner,
  FormCardInnerRow,
  FormTitle
} from './elements'
import { usePaymentForm } from './payment-form-provider'

const DateWrapper = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: ${breakpoints.phoneMax}) {
    flex-direction: column;
    align-items: flex-end;
  }
`

const StartDateText = styled(ParagraphSmall)`
  color: ${colors.grey56};
  margin-right: 8px;

  @media (max-width: ${breakpoints.phoneMax}) {
    margin: 0 0 8px;
  }
`

const StartDateBadge = styled(ParagraphSmall)`
  color: ${colors.white};
  font-weight: 500;
  background: ${colors.blue56};
  border-radius: 8px;
  padding: 4px 8px;
  min-width: 76px;
  text-align: center;
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const RadioWrapper = styled.div`
  display: flex;
  width: 100%;
  margin: 16px 0 24px;
  font-family: Inter, sans-serif;
  gap: 0 16px;
`

const taxPlaceholder = [
  {
    name: 'GST / PST'
  }
]

const getTaxLabel = value => {
  if (value === 'PST') return 'PST / QST'
  if (value === 'GST') return 'GST / HST'
  return value
}

const SummarySection = ({ date, subscriptionId }) => {
  const { t } = useTranslation('pricing')
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  const dispatch = useDispatch()
  const router = useRouter()
  const { query, locale } = router

  const catalog = useSelector(path(['plans', 'catalog'])) || {}
  const { values, state } = usePaymentForm()
  const frequency = values.frequency
  const accountType = values.account_type
  const paymentPlan = getPaymentPlan(accountType, frequency)
  const stateWithDefalt = state || 'BC'

  const planData = usePricingPlans()
  const pricingPlanData = planData[accountType]
  const productData =
    find(propEq('plan_name', paymentPlan))(catalog.products || []) || {}

  const queryPromoCode = query.couponCode
  const [invalidPromoCodeMessage, setInvalidPromoCodeMessage] = useState('')
  const couponData = find(propEq('type', 'coupon'), productData.discounts || [])
  const hasCouponData = !isEmptyOrNull(couponData)

  const handleRemoveCouponCode = useCallback(() => {
    const newQuery = omit(['couponCode'], query)
    router.replace(
      {
        pathname: '/register',
        query: newQuery
      },
      undefined,
      { shallow: true }
    )
  }, [router, query])

  useEffect(() => {
    const load = async () => {
      const body = await dispatch.plans.loadCatalog({
        province: stateWithDefalt,
        plan: paymentPlan,
        couponCode: queryPromoCode
      })
      if (body.error) {
        setInvalidPromoCodeMessage(body.message)
        handleRemoveCouponCode()
      }
    }
    load()
  }, [
    dispatch.plans,
    stateWithDefalt,
    paymentPlan,
    queryPromoCode,
    handleRemoveCouponCode
  ])

  const billingPeriod = productData.billing_period ?? 'MONTHLY'

  const frequencyNouns = {
    MONTHLY: t('pricing:month'),
    ANNUAL: t('pricing:year')
  }
  const frequencyText = frequencyNouns[billingPeriod]

  const startDateText = displayMobile
    ? t('pricing:Start date')
    : t('pricing:Subscription start date')

  const mulitplier = productData.regular_plan_billing_period_multiplier
  const subtotal = queryPromoCode
    ? productData.regular_price * mulitplier
    : productData.value
  const promoDiscount = productData.discount_from_regular_price
  const showTax =
    !!state &&
    catalog?.intended_province?.toLowerCase() === state?.toLowerCase()
  // At some point this may have fees, but for now its just tax 💰
  const taxes = showTax ? productData.fees || taxPlaceholder : taxPlaceholder
  const [discount, setDiscount] = useState(null)
  const [total, setTotal] = useState(productData.total_due)
  // TODO: get taxes from prorataCalculator along with discount/total?
  const [loading, setLoading] = useState(true)
  const [totalLoading, setTotalLoading] = useState(false)

  const displayTotal = showTax ? total : subtotal - promoDiscount

  useEffect(() => {
    const getDiscount = async () => {
      if (!paymentPlan || !subscriptionId) return
      const request = {
        plan_name: paymentPlan,
        state: stateWithDefalt,
        original_subscription_id: subscriptionId
      }
      setTotalLoading(true)
      const query = {
        ...(queryPromoCode ? { couponCode: queryPromoCode } : {})
      }
      const queryString = stringify(query)
      const { response, body } = await prorataCalculator(
        request,
        {},
        queryString
      )
      if (response.ok) {
        setDiscount(body.total_credit_adj)
        setTotal(body.total_amount)
      }
      setTotalLoading(false)
      setLoading(false)
    }
    getDiscount()
  }, [paymentPlan, stateWithDefalt, subscriptionId, queryPromoCode])

  return (
    <>
      <FormTitle text={t('pricing:Your Order Summary')}>
        <DateWrapper>
          <StartDateText>{startDateText}</StartDateText>
          <StartDateBadge>{date}</StartDateBadge>
        </DateWrapper>
      </FormTitle>
      <FormCard>
        <ContentWrapper>
          <Label fontSize="16px" type="large">
            {t('pricing:Billing Period')}
          </Label>
          <RadioWrapper>
            <FieldRadioButton
              id="monthly"
              name="frequency"
              value="monthly"
              disabled={totalLoading}
            >
              {t('pricing:Billing monthly')}
            </FieldRadioButton>
            <FieldRadioButton
              id="annual"
              name="frequency"
              value="annual"
              disabled={totalLoading}
            >
              {t('pricing:Billing yearly')}
            </FieldRadioButton>
          </RadioWrapper>
          {loading ? (
            <Skeleton width="100%" height={194} borderRadius={8} />
          ) : (
            <FormCardInner>
              <FormCardInnerRow>
                <ParagraphSmall>
                  <Trans
                    i18nKey="pricing:liv.rent plan type billing summary"
                    values={{
                      frequency: frequencyText,
                      planName: pricingPlanData?.name
                    }}
                    defaults="Liv.rent {{ planName }} plan x 1 {{ frequency }} "
                  />
                </ParagraphSmall>
                <ParagraphSmall>
                  <CurrencyFormatter
                    value={subtotal}
                    fallback="--.--"
                    fixedDecimalScale={2}
                    locale={locale}
                  />
                </ParagraphSmall>
              </FormCardInnerRow>
              {discount && discount !== '0' && (
                <FormCardInnerRow>
                  <ParagraphSmall>
                    <Trans
                      i18nKey="pricing:liv.rent mid-cycle discount summary"
                      defaults="Mid-billing cycle upgrade discount"
                    />
                  </ParagraphSmall>
                  <ParagraphSmall>
                    <CurrencyFormatter
                      value={discount}
                      fallback="--.--"
                      fixedDecimalScale={2}
                      isDeduction
                      locale={locale}
                    />
                  </ParagraphSmall>
                </FormCardInnerRow>
              )}
              {queryPromoCode && (
                <FormCardInnerRow>
                  <ParagraphSmall>
                    <Trans
                      i18nKey="pricing:Coupon code discount"
                      defaults="Coupon code discount"
                    />
                  </ParagraphSmall>
                  <ParagraphSmall>
                    <CurrencyFormatter
                      value={promoDiscount}
                      fallback="--.--"
                      fixedDecimalScale={2}
                      isDeduction
                      locale={locale}
                    />
                  </ParagraphSmall>
                </FormCardInnerRow>
              )}
              {taxes.map(({ name, ...rest }) => (
                <FormCardInnerRow key={`${name}-${values.state || 'none'}`}>
                  <ParagraphSmall>{getTaxLabel(name)}</ParagraphSmall>
                  <ParagraphSmall>
                    <CurrencyFormatter
                      {...rest}
                      fallback="--.--"
                      fixedDecimalScale={2}
                      locale={locale}
                    />
                  </ParagraphSmall>
                </FormCardInnerRow>
              ))}
              <FormCardInnerRow divider>
                <ParagraphMedium>{t('pricing:Total due')}</ParagraphMedium>
                {totalLoading ? (
                  <Skeleton width={100} height={20} />
                ) : (
                  <ParagraphMedium>
                    <CurrencyFormatter
                      fallback="--.--"
                      value={displayTotal}
                      fixedDecimalScale={2}
                      locale={locale}
                    />{' '}
                    CAD
                  </ParagraphMedium>
                )}
              </FormCardInnerRow>
              {!showTax && (
                <Label fontSize="12px" type="medium">
                  {t(
                    'pricing:*Taxes will be calculated when you enter a billing address'
                  )}
                </Label>
              )}
            </FormCardInner>
          )}
        </ContentWrapper>
        <CouponSection
          totalLoading={totalLoading}
          handleRemoveCouponCode={handleRemoveCouponCode}
          hasCouponData={hasCouponData}
          couponData={couponData}
          invalidPromoCodeMessage={invalidPromoCodeMessage}
          setInvalidPromoCodeMessage={setInvalidPromoCodeMessage}
        />
      </FormCard>
    </>
  )
}

export default SummarySection
